/**
 * Procedural audio: every sound is synthesized (see sounds.ts), so the game ships no audio
 * files. Buffers are rendered lazily on first use and in the background after unlock. Positional
 * playback uses panner nodes with distance variants, per-sound and global voice limits.
 */
import { effect } from '@preact/signals';
import type { WeaponDef } from '@vm/shared';
import { DEATH_FX_BY_ID } from '@vm/content';
import { hud } from '../hud/state';
import { generateSound, SOUNDS } from './sounds';

export { generateSound, SOUNDS } from './sounds';

interface Voice {
  name: string;
  src: AudioBufferSourceNode;
  gain: GainNode;
  started: number;
}

/** Beyond this distance a sound's `_far` variant (muffled, echoing) is used when it exists. */
const FAR_DISTANCE = 26;
/** Positional sounds farther than this are not played at all. */
const MAX_DISTANCE = 90;
const MAX_VOICES = 48;
/** Sounds that should not get random pitch variation (musical/UI cues). */
const STABLE = /^(ui_|capture|flag_|round_|levelup|reward|unlock|match_found|countdown|wave_|objective|streak|kill|headshot|notify|chat|gems|coins|xp_tick|respawn|heal|pickup|pet_(heal|reform|spotted))/;

function voiceLimit(name: string): number {
  if (name.startsWith('ui_') || name.startsWith('loop_')) return 3;
  if (name.startsWith('step')) return 8;
  if (name.startsWith('break_') || name.startsWith('explosion')) return 6;
  if (name === 'minigun' || name === 'w_voxel_shredder' || name.endsWith('smg') || name.startsWith('w_hornet')) return 10;
  return 6;
}

/** Background generation order: combat first, then everything else. */
function priority(name: string): number {
  if (name.endsWith('_far')) return 3;
  if (/^(w_|rifle|smg|pistol|shotgun|sniper|lmg|minigun|revolver|hit|headshot|kill|hurt|step|land|jump|explosion|burst|break_|swing|mag_|rack|equip_)/.test(name)) return 0;
  if (name.startsWith('loop_')) return 1;
  return 2;
}

export class AudioEngine {
  ctx: AudioContext | null = null;
  private master!: GainNode;
  private sfx!: GainNode;
  private musicGain!: GainNode;
  private buffers = new Map<string, AudioBuffer>();
  private pending: string[] = [];
  private worker: Worker | null = null;
  private requested = new Set<string>();
  private voices: Voice[] = [];
  private loops = new Map<string, { src: AudioBufferSourceNode; gain: GainNode; panner: PannerNode | null; name: string }>();
  private listener = { x: 0, y: 0, z: 0 };
  private unlockCallbacks: ((ctx: AudioContext) => void)[] = [];
  private lowHealth = false;
  volume = 0.8;
  sfxVolume = 1;
  musicVolume = 0.5;
  private musicNode: { stop(): void } | null = null;

  /** Must be called from a user gesture (browsers block audio until then). */
  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.master.gain.value = this.externalMute ? 0 : this.volume;
    // A gentle limiter keeps stacked explosions from clipping.
    const limiter = this.ctx.createDynamicsCompressor();
    limiter.threshold.value = -6;
    limiter.knee.value = 6;
    limiter.ratio.value = 8;
    limiter.attack.value = 0.003;
    limiter.release.value = 0.2;
    this.master.connect(limiter);
    limiter.connect(this.ctx.destination);
    this.sfx = this.ctx.createGain();
    this.sfx.gain.value = this.sfxVolume;
    this.sfx.connect(this.master);
    this.musicGain = this.ctx.createGain();
    this.musicGain.gain.value = this.musicVolume;
    this.musicGain.connect(this.master);
    const order = Object.keys(SOUNDS).sort((a, b) => priority(a) - priority(b));
    // Instant UI feedback: the tiniest sounds render right away; the rest come from a worker.
    for (const n of ['ui_click', 'ui_hover', 'ui_tab', 'ui_back', 'ui_toggle']) this.buffer(n);
    try {
      this.worker = new Worker(new URL('./soundbank.worker.ts', import.meta.url), { type: 'module' });
      this.worker.onmessage = (e: MessageEvent<{ name: string; data: Float32Array }>) => this.store(e.data.name, e.data.data);
      this.worker.onerror = () => {
        this.worker = null;
        this.pending = order.filter((n) => !this.buffers.has(n));
        this.warm();
      };
      this.worker.postMessage({ type: 'init', sr: this.ctx.sampleRate, names: order });
    } catch {
      this.worker = null;
      this.pending = order;
      this.warm();
    }
    effect(() => this.setLowHealth(hud.lowHealth.value && hud.active.value));
    for (const cb of this.unlockCallbacks) cb(this.ctx);
    this.unlockCallbacks = [];
  }

  /** Runs `cb` once the audio context exists (immediately if it already does). */
  onUnlock(cb: (ctx: AudioContext) => void): void {
    if (this.ctx) cb(this.ctx);
    else this.unlockCallbacks.push(cb);
  }

  /** A web portal asked for silence (CrazyGames `muteAudio`); it wins over the player's volume until it is lifted. */
  private externalMute = false;

  setExternalMute(muted: boolean): void {
    this.externalMute = muted;
    if (this.ctx) this.master.gain.value = muted ? 0 : this.volume;
  }

  setVolumes(master: number, sfx: number, music: number): void {
    this.volume = master;
    this.sfxVolume = sfx;
    this.musicVolume = music;
    if (!this.ctx) return;
    this.master.gain.value = this.externalMute ? 0 : master;
    this.sfx.gain.value = sfx;
    this.musicGain.gain.value = music;
  }

  /** Renders pending buffers in small idle slices so unlocking never hitches. */
  private warm(): void {
    const step = (): void => {
      const t0 = performance.now();
      while (this.pending.length && performance.now() - t0 < 6) {
        const name = this.pending.shift()!;
        if (!this.buffers.has(name)) this.buffer(name);
      }
      if (this.pending.length) setTimeout(step, 16);
    };
    setTimeout(step, 30);
  }

  private store(name: string, data: Float32Array): AudioBuffer | null {
    const ctx = this.ctx;
    if (!ctx) return null;
    const have = this.buffers.get(name);
    if (have) return have;
    const buf = ctx.createBuffer(1, data.length, ctx.sampleRate);
    buf.copyToChannel(data as Float32Array<ArrayBuffer>, 0);
    this.buffers.set(name, buf);
    return buf;
  }

  /** The buffer for a sound, rendering it now if needed. */
  private buffer(name: string): AudioBuffer | null {
    const hit = this.buffers.get(name);
    if (hit) return hit;
    const ctx = this.ctx;
    if (!ctx) return null;
    const data = generateSound(name, ctx.sampleRate);
    return data ? this.store(name, data) : null;
  }

  /**
   * Buffer if ready. While the worker is still rendering the bank, a missing sound is moved to
   * the front of its queue instead of stalling the frame (it plays from the next request on).
   */
  private ready(name: string): AudioBuffer | null {
    const hit = this.buffers.get(name);
    if (hit || !(name in SOUNDS)) return hit ?? null;
    if (!this.worker) return this.buffer(name);
    if (!this.requested.has(name)) {
      this.requested.add(name);
      this.worker.postMessage({ type: 'want', name });
    }
    return null;
  }

  has(name: string): boolean {
    return name in SOUNDS;
  }

  private panner(x: number, y: number, z: number, maxDist = 60): PannerNode {
    const ctx = this.ctx!;
    const p = ctx.createPanner();
    p.panningModel = 'equalpower';
    p.distanceModel = 'inverse';
    p.refDistance = 3;
    p.maxDistance = maxDist;
    p.rolloffFactor = 1.1;
    p.positionX.value = x;
    p.positionY.value = y;
    p.positionZ.value = z;
    p.connect(this.sfx);
    return p;
  }

  private stopVoice(v: Voice): void {
    try {
      v.gain.gain.setTargetAtTime(0, this.ctx!.currentTime, 0.01);
      v.src.stop(this.ctx!.currentTime + 0.05);
    } catch {
      /* already stopped */
    }
    const i = this.voices.indexOf(v);
    if (i >= 0) this.voices.splice(i, 1);
  }

  /**
   * Plays a one-shot. `pos` = world position (null/undefined = non-positional, e.g. our own
   * gun). Distant positional sounds switch to their `_far` variant automatically.
   */
  play(name: string, pos?: { x: number; y: number; z: number } | null, volume = 1, rate = 1): void {
    const ctx = this.ctx;
    if (!ctx || ctx.state !== 'running') return;
    let key = name;
    if (pos) {
      const l = this.listener;
      const d = Math.hypot(pos.x - l.x, pos.y - l.y, pos.z - l.z);
      if (d > MAX_DISTANCE) return;
      if (d > FAR_DISTANCE && `${name}_far` in SOUNDS) key = `${name}_far`;
    }
    const buf = this.ready(key) ?? (key !== name ? this.ready(name) : null);
    if (!buf) return;
    const same = this.voices.filter((v) => v.name === key);
    if (same.length >= voiceLimit(key)) this.stopVoice(same[0]);
    if (this.voices.length >= MAX_VOICES) this.stopVoice(this.voices[0]);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = rate * (STABLE.test(key) ? 1 : 0.95 + Math.random() * 0.1);
    const gain = ctx.createGain();
    gain.gain.value = volume;
    src.connect(gain);
    gain.connect(pos ? this.panner(pos.x, pos.y, pos.z, key.startsWith('explosion') ? 140 : 60) : this.sfx);
    src.start();
    const voice: Voice = { name: key, src, gain, started: ctx.currentTime };
    this.voices.push(voice);
    src.onended = () => {
      const i = this.voices.indexOf(voice);
      if (i >= 0) this.voices.splice(i, 1);
      gain.disconnect();
    };
  }

  /** Starts/updates/stops a named looping sound (beams, jetpacks, heartbeat). */
  loop(key: string, name: string | null, pos?: { x: number; y: number; z: number }, volume = 0.6): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const cur = this.loops.get(key);
    if (!name || (cur && cur.name !== name)) {
      if (cur) {
        cur.gain.gain.setTargetAtTime(0, ctx.currentTime, 0.05);
        cur.src.stop(ctx.currentTime + 0.3);
        this.loops.delete(key);
      }
      if (!name) return;
    } else if (cur) {
      if (pos && cur.panner) {
        cur.panner.positionX.value = pos.x;
        cur.panner.positionY.value = pos.y;
        cur.panner.positionZ.value = pos.z;
      }
      cur.gain.gain.setTargetAtTime(volume, ctx.currentTime, 0.05);
      return;
    }
    const buf = this.ready(name);
    if (!buf) return;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.gain.setTargetAtTime(volume, ctx.currentTime, 0.04);
    const panner = pos ? this.panner(pos.x, pos.y, pos.z) : null;
    src.connect(gain);
    gain.connect(panner ?? this.sfx);
    src.start();
    this.loops.set(key, { src, gain, panner, name });
  }

  /** Low-health heartbeat (bound to the HUD's lowHealth flag automatically after unlock). */
  setLowHealth(on: boolean): void {
    if (on === this.lowHealth) return;
    this.lowHealth = on;
    this.loop('heartbeat', on ? 'loop_heartbeat' : null, undefined, 0.55);
  }

  setListener(x: number, y: number, z: number, fx: number, fz: number): void {
    this.listener.x = x;
    this.listener.y = y;
    this.listener.z = z;
    const ctx = this.ctx;
    if (!ctx) return;
    const l = ctx.listener;
    if (l.positionX) {
      l.positionX.value = x;
      l.positionY.value = y;
      l.positionZ.value = z;
      l.forwardX.value = fx;
      l.forwardY.value = 0;
      l.forwardZ.value = fz;
      l.upX.value = 0;
      l.upY.value = 1;
      l.upZ.value = 0;
    } else {
      l.setPosition(x, y, z);
      l.setOrientation(fx, 0, fz, 0, 1, 0);
    }
  }

  /** Background music handle; the procedural sequencer lives in music.ts. */
  setMusic(node: { stop(): void } | null): void {
    if (node === this.musicNode) return;
    this.musicNode?.stop();
    this.musicNode = node;
  }

  get musicOut(): GainNode | null {
    return this.ctx ? this.musicGain : null;
  }

  /** Stops all sound effects and loops (music keeps playing). */
  stopAll(): void {
    for (const v of [...this.voices]) this.stopVoice(v);
    this.voices = [];
    for (const k of [...this.loops.keys()]) this.loop(k, null);
    this.lowHealth = false;
  }
}

export const audio = new AudioEngine();

const LOOP_KEYS: Record<string, string> = {
  mining: 'loop_mining',
  flame: 'loop_flame',
  tesla: 'loop_tesla',
};

/**
 * Sound for a weapon: pass the WeaponDef for a per-weapon sound (every weapon is distinct), or a
 * sound key (def.sound) for the shared bank. Beam weapons map to their loop buffers.
 */
export function weaponSound(key: string | WeaponDef): string {
  if (typeof key !== 'string') {
    if (LOOP_KEYS[key.sound]) return LOOP_KEYS[key.sound];
    const per = `w_${key.id}`;
    return per in SOUNDS ? per : weaponSound(key.sound);
  }
  return LOOP_KEYS[key] ?? key;
}

/** Whoosh for a melee swing. */
export function swingSound(def: WeaponDef): string {
  switch (def.model.archetype) {
    case 'hammer':
    case 'pickaxe':
      return 'swing_heavy';
    case 'katana':
      return 'swing_blade';
    case 'sword':
      return 'swing_energy';
    case 'buzzblade':
      return 'saw';
    default:
      return 'swing';
  }
}

// MatKind: 0 generic, 1 stone, 2 dirt, 3 wood, 4 metal, 5 glass, 6 leaves, 7 sand, 8 snow, 9 brick,
// 10 concrete, 11 plastic, 12 crystal, 13 cloth, 14 ice, 15 bedrock.
const BREAK_BY_KIND = ['stone', 'stone', 'soft', 'wood', 'metal', 'glass', 'leaves', 'soft', 'snow', 'brick', 'concrete', 'plastic', 'crystal', 'soft', 'ice', 'stone'];
const STEP_BY_KIND = ['stone', 'stone', 'grass', 'wood', 'metal', 'glass', 'grass', 'sand', 'snow', 'stone', 'stone', 'wood', 'glass', 'grass', 'glass', 'stone'];

export function breakSound(kind: number): string {
  return `break_${BREAK_BY_KIND[kind] ?? 'stone'}`;
}

let stepFlip = false;
/** Footstep for the ground material under the player, or a splash when wading (alternates two variants). */
export function stepSound(kind: number, wading = false): string {
  stepFlip = !stepFlip;
  return `step_${wading ? 'water' : (STEP_BY_KIND[kind] ?? 'stone')}_${stepFlip ? 'a' : 'b'}`;
}

/** Burst sound for a death effect id or style name. */
export function deathSound(style?: string | null): string {
  const s = (style && DEATH_FX_BY_ID[style]?.style) || style || 'shatter';
  switch (s) {
    case 'confetti':
      return 'burst_confetti';
    case 'pixel':
      return 'burst_pixel';
    case 'fireworks':
      return 'burst_fireworks';
    case 'bubbles':
      return 'burst_bubbles';
    case 'gold':
      return 'burst_gold';
    default:
      return 'burst';
  }
}
